"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { ORGANIZATION_TYPES } from "@/lib/constants";

export function RegisterForm() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    orgName: "",
    orgType: ORGANIZATION_TYPES[0],
    orgCity: "",
    orgWebsite: "",
    orgDescription: "",
    name: "",
    email: "",
    password: "",
  });

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    const data = await res.json();

    if (!res.ok) {
      toast.error(data.error ?? "No se pudo completar el registro");
      setLoading(false);
      return;
    }

    const result = await signIn("credentials", {
      email: form.email,
      password: form.password,
      redirect: false,
    });
    setLoading(false);

    if (result?.error) {
      toast.success("Cuenta creada. Ya puedes iniciar sesión.");
      router.push("/login");
      return;
    }
    toast.success("¡Bienvenido a Cultap!");
    router.push("/panel");
    router.refresh();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <h2 className="font-heading text-sm font-semibold text-muted-foreground">
          Datos de la organización
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="orgName">Nombre</Label>
            <Input
              id="orgName"
              required
              value={form.orgName}
              onChange={(e) => update("orgName", e.target.value)}
              placeholder="Librería Central"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="orgType">Tipo</Label>
            <Select
              id="orgType"
              value={form.orgType}
              onChange={(e) => update("orgType", e.target.value)}
            >
              {ORGANIZATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="orgCity">Ciudad</Label>
            <Input
              id="orgCity"
              required
              value={form.orgCity}
              onChange={(e) => update("orgCity", e.target.value)}
              placeholder="Madrid"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="orgWebsite">Web (opcional)</Label>
            <Input
              id="orgWebsite"
              value={form.orgWebsite}
              onChange={(e) => update("orgWebsite", e.target.value)}
              placeholder="https://…"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orgDescription">Descripción breve (opcional)</Label>
          <Textarea
            id="orgDescription"
            value={form.orgDescription}
            onChange={(e) => update("orgDescription", e.target.value)}
            placeholder="Cuéntanos brevemente quiénes sois"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="font-heading text-sm font-semibold text-muted-foreground">
          Cuenta de acceso
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="name">Persona de contacto</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Contraseña</Label>
          <Input
            id="password"
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
          />
        </div>
      </div>

      <Button type="submit" className="w-full" size="lg" disabled={loading}>
        {loading ? "Creando cuenta…" : "Crear cuenta y empezar a publicar"}
      </Button>
    </form>
  );
}
